import type { FriendsResponse } from '../../shared/types/social'
import { getFriends, invalidateFriendsRequest } from '../api/social'

const friendsListeners = new Set<(data: FriendsResponse) => void>()

/** Subscribe to the same accepted response used by the notification dot. */
export function subscribeFriendsUpdates(
	listener: (data: FriendsResponse) => void,
): () => void {
	friendsListeners.add(listener)
	return () => {
		friendsListeners.delete(listener)
	}
}

let active = false
let requestSequence = 0
let hasIncomingRequests = false
let resumeTimer: ReturnType<typeof setTimeout> | undefined

/** Shares reads and orders updates from both the overlay and background events. */
export async function loadFriendsWithIndicator(): Promise<FriendsResponse> {
	const sequence = ++requestSequence
	const data = await getFriends()
	if (active && sequence === requestSequence) {
		hasIncomingRequests = data.incoming.length > 0
		renderFriendRequestIndicator()
		for (const listener of friendsListeners) listener(data)
	}
	return data
}

export function renderFriendRequestIndicator(): void {
	const visible = hasIncomingRequests
	for (const button of document.querySelectorAll<HTMLElement>(
		'[data-friend-request-indicator]',
	)) {
		button.classList.toggle('has-friend-request', visible)
		const label = button.dataset.friendRequestIndicator
		if (label) {
			button.setAttribute(
				'aria-label',
				visible ? `${label}, new friend request` : label,
			)
		}
	}
}

export function startFriendRequestIndicator(): void {
	if (active) return
	active = true
	window.addEventListener('focus', refreshOnResume)
	window.addEventListener('online', refreshOnResume)
	document.addEventListener('visibilitychange', refreshOnResume)
	void refreshFriendRequestIndicator()
}

export function stopFriendRequestIndicator(): void {
	if (!active) return
	active = false
	requestSequence++
	hasIncomingRequests = false
	invalidateFriendsRequest()
	clearTimeout(resumeTimer)
	window.removeEventListener('focus', refreshOnResume)
	window.removeEventListener('online', refreshOnResume)
	document.removeEventListener('visibilitychange', refreshOnResume)
	renderFriendRequestIndicator()
}

export async function refreshFriendRequestIndicator(): Promise<void> {
	if (!active) return
	try {
		await loadFriendsWithIndicator()
	} catch (error) {
		console.warn('Failed to check friend requests', error)
	}
}

function refreshOnResume(): void {
	if (document.visibilityState === 'visible') {
		clearTimeout(resumeTimer)
		resumeTimer = setTimeout(
			() => void refreshFriendRequestIndicator(),
			100,
		)
	}
}
